import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { convertUnitToEnum } from '../utils/unitConversion';
import { calculateBulkPricing } from '../utils/bulkPricing';
import { ValidUnitValue } from '@/types/unit-types';

export interface LocalLineItem {
  id: string;
  estimate_id: string;
  component_type: string;
  component_id: string;
  description: string;
  base_unit: ValidUnitValue;
  quantity_in_base_units: number;
  unit_cost_snapshot: number;
  total_cost_snapshot: number;
  sort_order: number;
  manual_override?: boolean;
  manual_unit_cost?: number | null;
  manual_total_cost?: number | null;
  manual_description?: string | null;
  original_description?: string | null;
  category_id?: string | null;
  option_group_id?: string | null;
  supplier_categories?: any;
  // Track if this is a new item (not yet in DB)
  _isNew?: boolean;
  // Track if this item was deleted locally
  _isDeleted?: boolean;
  // Track original values for change detection
  _originalQuantity?: number;
  _originalUnitCost?: number;
}

interface UseLocalLineItemsProps {
  estimateId: string;
  initialLineItems: any[] | undefined;
  isLoading: boolean;
}

export const useLocalLineItems = ({ estimateId, initialLineItems, isLoading }: UseLocalLineItemsProps) => {
  // Local state for line items - this is the "working copy"
  const [localLineItems, setLocalLineItems] = useState<LocalLineItem[]>([]);
  // Snapshot of the original line items when we started editing
  const [baselineSnapshot, setBaselineSnapshot] = useState<LocalLineItem[]>([]);
  // Track if we've initialized from DB data
  const [isInitialized, setIsInitialized] = useState(false);
  // Track previous initialLineItems length to detect late-arriving data
  const prevInitialLengthRef = useRef<number>(0);

  // Initialize local state from DB data
  // IMPORTANT: Wait for initialLineItems to be DEFINED (not undefined) before initializing
  // This prevents race conditions where we initialize with empty data before version snapshot loads
  useEffect(() => {
    // Only proceed if not loading and initialLineItems is explicitly defined (not undefined)
    if (!isLoading && initialLineItems !== undefined) {
      if (!isInitialized) {
        // First initialization
        const items = initialLineItems.map(item => ({
          ...item,
          _isNew: false,
          _isDeleted: false,
          _originalQuantity: item.quantity_in_base_units,
          _originalUnitCost: item.unit_cost_snapshot,
        }));
        console.log('[useLocalLineItems] Initializing with items:', items.length);
        setLocalLineItems(items);
        setBaselineSnapshot(JSON.parse(JSON.stringify(items)));
        setIsInitialized(true);
        prevInitialLengthRef.current = initialLineItems.length;
      } else if (prevInitialLengthRef.current === 0 && initialLineItems.length > 0) {
        // Safeguard: Re-initialize if we previously initialized with empty data
        // but now have real data (handles race condition edge case)
        console.log('[useLocalLineItems] Re-initializing: data arrived after empty init, items:', initialLineItems.length);
        const items = initialLineItems.map(item => ({
          ...item,
          _isNew: false,
          _isDeleted: false,
          _originalQuantity: item.quantity_in_base_units,
          _originalUnitCost: item.unit_cost_snapshot,
        }));
        setLocalLineItems(items);
        setBaselineSnapshot(JSON.parse(JSON.stringify(items)));
        prevInitialLengthRef.current = initialLineItems.length;
      }
    }
  }, [initialLineItems, isLoading, isInitialized]);

  // Reset when estimate changes
  useEffect(() => {
    console.log('[useLocalLineItems] Resetting for new estimate:', estimateId);
    setIsInitialized(false);
    setLocalLineItems([]);
    setBaselineSnapshot([]);
    prevInitialLengthRef.current = 0;
  }, [estimateId]);

  // Add item locally (no DB write)
  const addItemLocally = useCallback(async (item: any, componentType: string) => {
    // Check if item already exists
    const existingIndex = localLineItems.findIndex(
      li => li.component_id === item.id && li.component_type === componentType && !li._isDeleted
    );

    if (existingIndex !== -1) {
      // Update quantity of existing item
      setLocalLineItems(prev => {
        const updated = [...prev];
        const existingItem = updated[existingIndex];
        const newQuantity = existingItem.quantity_in_base_units + 1;
        
        // Recalculate pricing
        const pricingResult = calculateBulkPricingSync(componentType, item.id, newQuantity, existingItem.unit_cost_snapshot);
        
        updated[existingIndex] = {
          ...existingItem,
          quantity_in_base_units: newQuantity,
          unit_cost_snapshot: pricingResult.unitCost,
          total_cost_snapshot: pricingResult.totalCost,
        };
        return updated;
      });
      return { isUpdate: true };
    }

    // Create new local item
    let description = '';
    let baseUnit: ValidUnitValue = 'item';
    let unitCost = 0;

    if (componentType === 'internal_line_item') {
      description = item.name || item.title || item.description;
      baseUnit = convertUnitToEnum(item.base_unit || item.unit_symbol || 'item');
      unitCost =
        typeof item.final_price === 'number'
          ? item.final_price
          : item.base_cost && typeof item.markup_percentage === 'number'
          ? item.base_cost * (1 + item.markup_percentage / 100)
          : item.base_cost || 0;
    } else if (componentType === 'supplier_line_item') {
      description = item.name || item.title || item.description;
      baseUnit = convertUnitToEnum(item.base_unit || item.unit_symbol || 'item');
      unitCost =
        typeof item.final_price === 'number'
          ? item.final_price
          : item.base_cost && typeof item.markup_percentage === 'number'
          ? item.base_cost * (1 + item.markup_percentage / 100)
          : item.base_cost || 0;
    } else if (componentType === 'assembly') {
      description = item.name || item.description;
      baseUnit = 'item';
      unitCost = 0;
    } else if (componentType === 'system_line_item') {
      description = item.name || item.description;
      baseUnit = convertUnitToEnum(item.unit_symbol || item.base_unit || 'item');
      unitCost =
        typeof item.final_price === 'number'
          ? item.final_price
          : item.base_cost || 0;
    }

    const newItem: LocalLineItem = {
      id: `local_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      estimate_id: estimateId,
      component_type: componentType,
      component_id: item.id,
      description,
      base_unit: baseUnit,
      quantity_in_base_units: 1,
      unit_cost_snapshot: unitCost,
      total_cost_snapshot: unitCost,
      sort_order: localLineItems.filter(li => !li._isDeleted).length + 1,
      _isNew: true,
      _isDeleted: false,
    };

    setLocalLineItems(prev => [...prev, newItem]);
    return { isUpdate: false, item: newItem };
  }, [localLineItems, estimateId]);

  // Add custom item locally (no DB write) - for custom line items in local-first mode
  const addCustomItemLocally = useCallback((data: {
    description: string;
    quantity: number;
    unit: string;
    categoryId: string;
    baseCost: number;
    markupPercentage: number;
  }) => {
    const unitFinalPrice = data.baseCost * (1 + data.markupPercentage / 100);
    const totalFinalPrice = unitFinalPrice * data.quantity;
    
    const newItem: LocalLineItem = {
      id: `local_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      estimate_id: estimateId,
      component_type: 'custom',
      component_id: `custom_${Date.now()}`,
      description: data.description,
      base_unit: data.unit as ValidUnitValue,
      quantity_in_base_units: data.quantity,
      unit_cost_snapshot: unitFinalPrice,
      total_cost_snapshot: totalFinalPrice,
      manual_override: true,
      manual_unit_cost: data.baseCost,
      manual_total_cost: totalFinalPrice,
      category_id: data.categoryId,
      sort_order: localLineItems.filter(li => !li._isDeleted).length + 1,
      _isNew: true,
      _isDeleted: false,
    };

    setLocalLineItems(prev => [...prev, newItem]);
    return { item: newItem };
  }, [localLineItems, estimateId]);

  // Remove item locally (no DB write)
  const removeItemLocally = useCallback((lineItemId: string) => {
    setLocalLineItems(prev => {
      const itemIndex = prev.findIndex(li => li.id === lineItemId);
      if (itemIndex === -1) return prev;

      const item = prev[itemIndex];
      
      // If it's a new item, just remove it entirely
      if (item._isNew) {
        return prev.filter(li => li.id !== lineItemId);
      }
      
      // If it's an existing item, mark as deleted
      const updated = [...prev];
      updated[itemIndex] = { ...item, _isDeleted: true };
      return updated;
    });
  }, []);

  // Update quantity locally (no DB write)
  const updateQuantityLocally = useCallback(async (
    lineItemId: string, 
    quantity: number,
    componentType: string,
    componentId: string,
    baseCost: number
  ) => {
    // Calculate bulk pricing
    const pricingResult = await calculateBulkPricing(componentType, componentId, quantity, baseCost);

    setLocalLineItems(prev => {
      const updated = [...prev];
      const index = updated.findIndex(li => li.id === lineItemId);
      if (index === -1) return prev;

      updated[index] = {
        ...updated[index],
        quantity_in_base_units: quantity,
        unit_cost_snapshot: pricingResult.unitCost,
        total_cost_snapshot: pricingResult.totalCost,
      };
      return updated;
    });

    return pricingResult;
  }, []);

  // Get visible line items (excluding deleted)
  const visibleLineItems = useMemo(() => {
    return localLineItems.filter(li => !li._isDeleted);
  }, [localLineItems]);

  // Get changes for persistence
  const getChanges = useCallback(() => {
    const additions = localLineItems.filter(li => li._isNew && !li._isDeleted);
    const deletions = localLineItems.filter(li => !li._isNew && li._isDeleted);
    const modifications = localLineItems.filter(li => {
      if (li._isNew || li._isDeleted) return false;
      
      const original = baselineSnapshot.find(b => b.id === li.id);
      if (!original) return false;
      
      return (
        li.quantity_in_base_units !== original.quantity_in_base_units ||
        li.unit_cost_snapshot !== original.unit_cost_snapshot ||
        li.description !== original.description ||
        li.manual_override !== original.manual_override ||
        li.manual_unit_cost !== original.manual_unit_cost
      );
    });

    return { additions, deletions, modifications };
  }, [localLineItems, baselineSnapshot]);

  // Check if there are any changes
  const hasLineItemChanges = useCallback(() => {
    const { additions, deletions, modifications } = getChanges();
    return additions.length > 0 || deletions.length > 0 || modifications.length > 0;
  }, [getChanges]);

  // Reset local state to baseline
  const resetToBaseline = useCallback(() => {
    setLocalLineItems(JSON.parse(JSON.stringify(baselineSnapshot)));
  }, [baselineSnapshot]);

  return {
    localLineItems: visibleLineItems,
    allLocalLineItems: localLineItems,
    baselineSnapshot,
    isInitialized,
    addItemLocally,
    addCustomItemLocally,
    removeItemLocally,
    updateQuantityLocally,
    getChanges,
    hasLineItemChanges,
    resetToBaseline,
  };
};

// Synchronous version for immediate updates
function calculateBulkPricingSync(componentType: string, componentId: string, quantity: number, baseCost: number) {
  // Simple sync calculation - the async version will be used for accurate tier pricing
  return {
    unitCost: baseCost,
    totalCost: baseCost * quantity,
    hasBulkPricing: false,
    tierInfo: ''
  };
}
